/**
 * WelcomeIntro — first-load cinematic card.  Skippable, and only shown
 * once per browser session (sessionStorage flag).
 */
import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import ConnectionSourceLogo from '../../components/ConnectionSourceLogo';

const KEY = 'cs_onb_intro_shown';

export function WelcomeIntro() {
  const [shown, setShown] = useState(() => {
    if (typeof window === 'undefined') return false;
    try { return !sessionStorage.getItem(KEY); } catch { return false; }
  });

  useEffect(() => {
    if (!shown) return undefined;
    const t = setTimeout(() => {
      try { sessionStorage.setItem(KEY, '1'); } catch { /* ignore */ }
      setShown(false);
    }, 1900);
    return () => clearTimeout(t);
  }, [shown]);

  return (
    <AnimatePresence>
      {shown && (
        <motion.div
          key="welcome"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.4 }}
          className="fixed inset-0 z-[60] flex items-center justify-center p-4 backdrop-blur-xl"
          style={{ background: 'rgba(11,11,15,0.92)' }}
          onClick={() => {
            try { sessionStorage.setItem(KEY, '1'); } catch { /* ignore */ }
            setShown(false);
          }}
          data-testid="welcome-intro">
          <motion.div
            initial={{ scale: 0.92, opacity: 0, y: 12 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.96, opacity: 0 }}
            transition={{ duration: 0.55, ease: [0.2, 0.9, 0.3, 1] }}
            className="relative text-center px-6">
            <motion.div
              initial={{ scale: 0.5, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.15, duration: 0.6, type: 'spring', stiffness: 110 }}
              className="mb-5">
              <ConnectionSourceLogo size="lg" />
            </motion.div>
            <motion.div
              initial={{ y: 14, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.45, duration: 0.5 }}
              className="text-[10px] uppercase tracking-[0.42em] font-black mb-3"
              style={{ color: '#F59E0B' }}>
              Welcome
            </motion.div>
            <motion.h1
              initial={{ y: 18, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.6, duration: 0.55 }}
              className="text-3xl sm:text-5xl font-black leading-tight"
              style={{
                fontFamily: 'Outfit, sans-serif',
                background: 'linear-gradient(135deg, #CD040B, #F59E0B)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
              }}>
              Let&rsquo;s make it official.
            </motion.h1>
            <motion.p
              initial={{ y: 14, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.85, duration: 0.5 }}
              className="text-sm mt-3"
              style={{ color: '#A3A3A3' }}>
              Five minutes from here to "first day of work."
            </motion.p>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.4, duration: 0.4 }}
              className="text-[9px] mt-8 uppercase tracking-[0.32em]"
              style={{ color: '#737373' }}>
              Tap anywhere to begin
            </motion.div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
